import React from 'react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  helperText?: string;
}

export const Input: React.FC<InputProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  error,
  helperText,
  required = false,
  placeholder,
  disabled = false,
  className = '',
  autoComplete,
  ...rest
}) => {
  const hasError = Boolean(error);
  const inputId = `input-${name}`;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-charcoal mb-1.5"
        >
          {label}
          {required && <span className="text-danger-500 ml-0.5">*</span>}
        </label>
      )}

      <input
        id={inputId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete={autoComplete}
        {...rest}
        // The visible label, else the caller's aria-label, else the caller's own
        // <label htmlFor={`input-${name}`}>. Never `name`: falling back to it is how
        // lab fields were announced as "upc_ratio" and "lab-calcium_total" (finding 20).
        aria-label={label || rest['aria-label']}
        aria-required={required}
        aria-invalid={hasError}
        aria-describedby={
          hasError
            ? `${inputId}-error`
            : helperText
              ? `${inputId}-helper`
              : undefined
        }
        className={`
          w-full px-4 py-2.5 text-sm rounded-xl border transition-all duration-200
          bg-white placeholder:text-gray-400
          disabled:bg-sage-50 disabled:text-gray-600 disabled:cursor-not-allowed
          focus:outline-none
          ${
            hasError
              ? 'border-danger-400 focus:border-danger-500 focus:shadow-glow-danger'
              : 'border-faint hover:border-gray-300 focus:border-sage-500 focus:shadow-glow'
          }
          min-h-[44px]
        `}
      />

      {error && (
        <p
          id={`${inputId}-error`}
          className="mt-1.5 text-sm text-danger-600 font-medium animate-fade-in"
          role="alert"
        >
          {error}
        </p>
      )}

      {helperText && !error && (
        <p id={`${inputId}-helper`} className="mt-1.5 text-xs text-mid">
          {helperText}
        </p>
      )}
    </div>
  );
};
